import useMediaQuery from '@mui/material/useMediaQuery'
import {
    BlockWrapper,
    ChildrenBlock,
    defineBlock,
    useChildrenBlocks,
    useEditor,
} from '@smartfire/js-block-engine'
import { useRef, useState } from 'react'
import { Autoplay, Navigation, Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import 'swiper/css'
import 'swiper/css/pagination'
import Button from '../button/block'
import BackgroundCarousel from './BackgroundCarousel'
import { ReactComponent as ArrowSlideLeft } from '../assets/arrow-slide-left.svg'
import { ReactComponent as ArrowSlideRight } from '../assets/arrow-slide-right.svg'
import { ReactComponent as ColorsVertical } from './colors-vertical.svg'
import { ReactComponent as ShapeGreenSliderMobile } from './shape-green-slider-mobile.svg'
import { ReactComponent as ShapeGreenSlider } from './shape-green-slider.svg'
import style from './block.module.scss'
import { useTranslation } from 'react-i18next'

const TEMPLATE = [
    ['smartfire/home-hero---card-admin', {}],
    ['smartfire/home-hero---card-admin', {}],
    ['smartfire/home-hero---card-admin', {}],
]

const Block = () => {
    const { t } = useTranslation()
    const { isEditing } = useEditor()
    const { children } = useChildrenBlocks()

    const [swiperActiveIndex, setSwiperActiveIndex] = useState(0)
    const [buttonLink, setButtonLink] = useState()
    const [isCardHover, setIsCardHover] = useState(false)

    const swiperNextRef = useRef(null)
    const swiperPrevRef = useRef(null)
    const swiperPaginationRef = useRef(null)

    const isTablet = useMediaQuery(`(max-width: ${style.tabletBreakpoint})`)
    const mobileBreakpointInt = style.mobileBreakpoint.slice(0, 3)

    const formatFractionCurrent = (number) => {
        // Get accurate index instead of swiper.activeIndex because of loop
        setSwiperActiveIndex(number - 1)
        setButtonLink(children[number - 1]?.props?.node?.block?.props?.link)
        return number
    }

    const swiperProps = {
        allowTouchMove: true,
        slidesPerView: 1,
        spaceBetween: 10,
        loop: true,
        loopAdditionalSlides: 2,
        autoplay: { delay: 12000, disableOnInteraction: false },
        navigation: {
            prevEl: swiperPrevRef.current,
            nextEl: swiperNextRef.current,
        },
        pagination: {
            el: swiperPaginationRef.current,
            type: 'fraction',
            formatFractionCurrent,
        },
        breakpoints: {
            [mobileBreakpointInt]: {
                allowTouchMove: false,
            },
        },
        modules: [Autoplay, Navigation, Pagination],
    }

    // Initialize the swiper with React refs
    const initSwiper = (swiper) => {
        // Delay execution for the refs to be defined
        setTimeout(() => {
            // Re-init navigation
            if (swiper.params) {
                swiper.params.navigation.prevEl = swiperPrevRef.current
                swiper.params.navigation.nextEl = swiperNextRef.current
                swiper.navigation.destroy()
                swiper.navigation.init()
                swiper.navigation.update()

                // Re-init pagination
                swiper.params.pagination.el = swiperPaginationRef.current
                swiper.pagination.init()
                swiper.pagination.render()
                swiper.pagination.update()
            }
        })
    }

    return isEditing ? (
        <ChildrenBlock
            gutenbergEditorConfig={{
                allowedBlocks: ['smartfire/home-hero---card-admin'],
                template: TEMPLATE,
            }}
        />
    ) : (
        <BlockWrapper.Div className={style.front}>
            <BackgroundCarousel {...{ children, swiperActiveIndex, isCardHover }} />

            <div className={style.cardPosition}>
                <div className={style.cardContainer}>
                    {isTablet ? (
                        <ShapeGreenSliderMobile className={style.shapeGreenSlider} />
                    ) : (
                        <ShapeGreenSlider className={style.shapeGreenSlider} />
                    )}

                    <div
                        className={style.card}
                        onMouseEnter={() => setIsCardHover(true)}
                        onMouseLeave={() => setIsCardHover(false)}
                    >
                        <Swiper {...swiperProps} onSwiper={initSwiper}>
                            {children &&
                                children.map((child, i) => (
                                    <SwiperSlide key={i}>{child}</SwiperSlide>
                                ))}
                        </Swiper>

                        <div className={style.navContainer}>
                            <Button
                                text={t('DÉCOUVRIR')}
                                link={buttonLink}
                                aspect="contained"
                                color={style.darkBlue}
                                colorHover={style.blue}
                                classNames={style.button}
                                inspector={false}
                            />

                            <div className={style.nav}>
                                <div className={style.separator} />

                                <div ref={swiperPaginationRef} className={style.pagination} />

                                <div className={style.navArrows}>
                                    <div ref={swiperPrevRef} className={style.arrowLeft}>
                                        <ArrowSlideLeft />
                                    </div>
                                    <div ref={swiperNextRef} className={style.arrowRight}>
                                        <ArrowSlideRight />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <ColorsVertical className={style.colorsVertical} />
                    </div>
                </div>
            </div>
        </BlockWrapper.Div>
    )
}

export default defineBlock({
    Block,
    smartProps: {},
    setup: {
        title: 'Home Hero',
    },
})
